import AsyncStorage from "@react-native-async-storage/async-storage";
import { getDocumentAsync } from "expo-document-picker";
import * as FileSystem from "expo-file-system";
import { StorageAccessFramework } from "expo-file-system";
import { openURL } from "expo-linking";
import { shareAsync } from "expo-sharing";
import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FlatList,
  I18nManager,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import { Button, Card, Input, useThemeMode } from "react-native-elements";
import { Achievement } from "../backend/Achievement";
import { cleanup } from "../backend/Cleanup";
import { clearFlag, hasFlag, setFlag } from "../backend/Flags";
import {
  getNewGamePlusCount,
  triggerNewGamePlus,
} from "../backend/NewGamePlus";
import {
  GLOBAL_INITIAL_STATE,
  PLAYER_INITIAL_STATE,
  PlayerContext,
} from "../backend/PlayerContext";
import { Resource, grantResource } from "../backend/Resources";
import {
  loadBase64Data,
  makeCompatible,
  saveDataUnsafe,
  serializeSaveData,
} from "../backend/SaveData";
import {
  TransformationType,
  explainTransformations,
  registerTransformation,
} from "../backend/calculation/Calculation";
import { setGameEnding } from "../backend/endings/Endings";
import { getAllGameEvents, triggerEvent } from "../backend/events/Events";
import { debugRandomEvents } from "../backend/events/autorandom/RandomEventTrigger";
import { AttackTarget } from "../backend/exploration/AttackTarget";
import { CombatStat } from "../backend/exploration/CombatStats";
import { School, grantSchoolExp } from "../backend/spells/ElementsAndSchools";
import conjureBuilding from "../backend/spells/conjuration/ConjureBuilding";
import { grantTemporaryEffect } from "../backend/timetick/TemporaryEffects";
import { getSecondsPlayed } from "../backend/timetick/TotalTimePlayed";
import { unlockAchievement } from "../utils/Achievements";
import { clone } from "../utils/CoreUtils";
import { saveToFile } from "../utils/FilesystemSave";
import { formatNumber } from "../utils/FormattingUtils";
import { logAnalyticsEventGeneric } from "../utils/GameAnalytics";
import CategoryContainer from "./utility/CategoryContainer";
import { Text } from "./utility/Text";

export default function Debug() {
  const events = getAllGameEvents();
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const eventButtons = useMemo(
    () => (
      <CategoryContainer title="Trigger events">
        <Button
          title="Trigger random event"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              state.nextRandomEvent = 0;
              return state;
            })
          }
        />
        <FlatList
          data={Object.values(events)}
          renderItem={({ item }) => {
            return (
              <Button
                key={item.getId()}
                title={item.getId()}
                onPress={() =>
                  playerContextRef.current.apply(triggerEvent(item))
                }
              />
            );
          }}
          keyExtractor={(event) => event.getId()}
        />
      </CategoryContainer>
    ),
    [JSON.stringify(Object.keys(events))],
  );
  const playerContextJson = JSON.stringify(
    playerContext.exploration,
    null,
    "\t",
  );
  const [newPlayerContextState, setNewPlayerContextState] = useState(
    JSON.stringify(playerContext, null, "  "),
  );
  const [fixingPlayerContextState, setFixingPlayerContextState] = useState("");
  const overridePlayerContext = useCallback(async () => {
    const deserializedJSON = JSON.parse(newPlayerContextState);
    playerContextRef.current.overwrite((_oldState) => {
      return makeCompatible(deserializedJSON);
    });
    await saveDataUnsafe(deserializedJSON);
  }, [newPlayerContextState]);

  const [transformDebugInput, setTransformDebugInput] = useState("");
  const [transformDebugOutput, setTransformDebugOutput] = useState("");

  const calculate = useCallback(() => {
    const tags = transformDebugInput.split(",");

    const output = explainTransformations(tags, playerContextRef.current, 1.0);
    const debugOutput = Object.keys(output)
      .map((key) => {
        const eachType = output[key as TransformationType].map(
          ({ id, value }) =>
            `${id}: ${
              value != null
                ? formatNumber(value, { showDecimals: true })
                : "undefined"
            }`,
        );
        return key + ": " + eachType.join(", ");
      })
      .join("\n");
    setTransformDebugOutput(debugOutput);
  }, [transformDebugInput, setTransformDebugOutput]);

  const themeMode = useThemeMode();

  const [visible, setVisible] = useState(false);

  return (
    <View style={styles.container}>
      <CategoryContainer title="Other actions">
        <Button
          title="Clear Quickbar"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              state.quickbar = [];
              return state;
            })
          }
        />
        <Button
          title="Open Document Picker"
          onPress={async () => {
            const result = await getDocumentAsync();
            if (result.type == "success") {
              const resultContents = await FileSystem.readAsStringAsync(
                result.uri,
              );
              const unserialized = await loadBase64Data(resultContents);
              console.warn(unserialized);
            }
            console.warn(result);
          }}
        />
        <Button
          title="Save To Local Storage (Web Only)"
          onPress={async () => {
            const serializedData = await serializeSaveData(playerContext);
            await openURL(
              "data:application/octet-stream;utf8," + serializedData,
            );
          }}
        />
        <Button
          title="Save To Local Storage (iOS Only)"
          onPress={async () => {
            const directoryUri =
              (FileSystem.documentDirectory ?? "") + `/MagicResearch/`;
            const dirInfo = await FileSystem.getInfoAsync(directoryUri);
            console.warn(directoryUri);
            if (!dirInfo.exists) {
              const directoryCreateResults =
                await FileSystem.makeDirectoryAsync(directoryUri, {
                  intermediates: true,
                });
              console.warn("directoryCreated", directoryCreateResults);
            }
            const uri = `${directoryUri}magic-research-save-${Date.now()}.sav`;
            const serializedData = await serializeSaveData(
              playerContextRef.current,
            );
            const results = await FileSystem.writeAsStringAsync(
              uri,
              serializedData,
            );
            const shareResults = await shareAsync(uri, {
              dialogTitle: "Send Magic Research Save to...",
              UTI: "UTTypePlainText",
            });
            console.warn(results, shareResults);
          }}
        />
        <Button
          title="Save To Local Storage (Android Only)"
          onPress={async () => {
            // Gets SAF URI to the album
            const directoryUri =
              StorageAccessFramework.getUriForDirectoryInRoot("MagicResearch");

            // Requests permissions
            const permissions =
              await StorageAccessFramework.requestDirectoryPermissionsAsync(
                directoryUri,
              );
            if (!permissions.granted) {
              console.warn(permissions);
              return;
            }

            const permittedUri = permissions.directoryUri;
            const fileName = `magic-research-save-${Date.now()}.sav`;
            const saveDataUri = await StorageAccessFramework.createFileAsync(
              permittedUri,
              fileName,
              "text/text",
            );
            const serializedData = await serializeSaveData(
              playerContextRef.current,
            );
            const results = await FileSystem.writeAsStringAsync(
              saveDataUri,
              serializedData,
            );

            // const directoryUri =
            //   (FileSystem.documentDirectory ?? "") + `/MagicResearch/`;
            // const dirInfo = await FileSystem.getInfoAsync(directoryUri);
            // if (!dirInfo.exists) {
            //   const directoryCreateResults =
            //     await FileSystem.makeDirectoryAsync(directoryUri, {
            //       intermediates: true,
            //     });
            //   console.warn("directoryCreated", directoryCreateResults);
            // }
            // const uri = `${directoryUri}magic-research-save-${Date.now()}.sav`;
            // const serializedData = await serializeSaveData(
            //   playerContextRef.current,
            // );
            // const results = await FileSystem.writeAsStringAsync(
            //   uri,
            //   serializedData,
            // );
            // console.warn(results);
          }}
        />
        <Button
          title="Max All Storages"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              for (let resource in playerContextRef.current.resources) {
                if (resource != "TimePieces") {
                  state = grantResource(resource as Resource, 9999999)(state);
                }
              }
              // state = grantResource(Resource.TimePieces, 9999999)(state);
              return state;
            })
          }
        />
        <Button
          title="Toggle Machinegun Mode"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              if (hasFlag(state, "machinegun_mode")) {
                state = clearFlag("machinegun_mode")(state);
              } else {
                state = setFlag("machinegun_mode")(state);
              }
              return state;
            })
          }
        />
        <Button
          title="Run 100 Conjure Building Maxed Out"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              for (let i = 0; i < 100; i++) {
                // Max out resources
                for (let resource in playerContextRef.current.resources) {
                  if (resource != "TimePieces") {
                    state = grantResource(
                      resource as Resource,
                      Infinity,
                    )(state);
                  }
                }

                // Then run Conjure Building
                state = conjureBuilding.doAction({ isAutomatic: false }, state);
              }
              // state = grantResource(Resource.TimePieces, 9999999)(state);
              return state;
            })
          }
        />
        <Button
          title="Grant Dummy Temp Effects"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              for (let i = 0; i < 10; i++) {
                state = grantTemporaryEffect(
                  "dummy" + i,
                  "Dummy effect " + i,
                  60,
                  {
                    isEnemyTemporaryEffect: true,
                    combatOnly: true,
                  },
                )(state);
              }
              return state;
            })
          }
        />
        <Button
          title="Trigger unlock_exploration achievement"
          onPress={() => unlockAchievement(Achievement.UNLOCK_EXPLORATION)}
        />
        <Button title="Force RTL" onPress={() => I18nManager.forceRTL(true)} />
        <Button
          title="Grant 10k School Exp"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              state = grantSchoolExp(School.Conjuration, 10000)(state);
              state = grantSchoolExp(School.Enchantment, 10000)(state);
              state = grantSchoolExp(School.Illusion, 10000)(state);
              return state;
            })
          }
        />
        <Button
          title="Warp 10 minutes"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              state.warpTimeSecs = 60 * 10;
              return state;
            })
          }
        />
        <Button
          title="Artifically pass 10 minutes"
          onPress={() => {
            playerContextRef.current.apply((state) => {
              state.skipAheadTime = 60 * 10;
              return state;
            });
          }}
        />
        <Button
          title="Toggle 2x speed"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              if (hasFlag(state, "debug_2x_speed")) {
                state = clearFlag("debug_2x_speed")(state);
              } else {
                state = setFlag("debug_2x_speed")(state);
              }
              return state;
            })
          }
        />
        <Button
          title="Toggle 0x speed"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              if (hasFlag(state, "debug_0x_speed")) {
                state = clearFlag("debug_0x_speed")(state);
              } else {
                state = setFlag("debug_0x_speed")(state);
              }
              return state;
            })
          }
        />
        <Button
          title="Toggle invincibility"
          onPress={() =>
            playerContextRef.current.apply((state) => {
              if (hasFlag(state, "debug_invincibility")) {
                state = clearFlag("debug_invincibility")(state);
              } else {
                state = setFlag("debug_invincibility")(state);
              }
              return state;
            })
          }
        />
        <Button
          title="Send Test Analytics Event"
          onPress={() =>
            logAnalyticsEventGeneric(
              playerContextRef.current,
              "test_analytics_event",
              { testParam: Math.random() },
            )
          }
        />
        <Button
          onPress={() => {
            AsyncStorage.removeItem("PlayerContextState");
            AsyncStorage.removeItem("LastSaveSecondsPlayed");
          }}
          title={"Clear storage"}
          key="clearstorage"
        />
        <Button
          onPress={() => {
            playerContext.apply((state) => {
              state.global = GLOBAL_INITIAL_STATE;
              return state;
            });
          }}
          title={"Reset global state"}
          key="resetglobal"
        />
        <Button
          onPress={() => {
            playerContext.apply(cleanup);
          }}
          title={"Clean up playercontext"}
          key="cleanupplayercontext"
        />
        <Button
          onPress={() => {
            playerContext.apply((state) => {
              return setGameEnding(state, "standard_ending");
            });
          }}
          title={"Trigger standard ending"}
          key="triggerending"
        />
        <Button
          onPress={() => {
            playerContext.apply((state) =>
              triggerNewGamePlus(
                state,
                getNewGamePlusCount(state) + 1,
                getNewGamePlusCount(state) + 1,
              ),
            );
          }}
          title={"Trigger NG+"}
          key="triggerngplus"
        />
        <Button
          onPress={() => {
            playerContext.apply((state) => {
              return clone(PLAYER_INITIAL_STATE);
            });
            AsyncStorage.removeItem("PlayerContextState");
            AsyncStorage.removeItem("LastSaveSecondsPlayed");
          }}
          title={"Restart game (WARNING: ALSO DELETES SAVED DATA)"}
          key="restartgame"
        />
      </CategoryContainer>
      <Card>
        <Card.Title>Multiplier Debugging</Card.Title>
        <Text>Separate tags by commas</Text>
        <Input
          onChangeText={setTransformDebugInput}
          value={transformDebugInput}
          onSubmitEditing={calculate}
        />
        <Button onPress={calculate} title="Debug multipliers" />
        <Text>{transformDebugOutput}</Text>
      </Card>
      <Card>
        <Card.Title>Random Event Debugging</Card.Title>
        <Text>
          Next event in{" "}
          {formatNumber(
            playerContext.nextRandomEvent - getSecondsPlayed(playerContext),
          )}{" "}
          sec
        </Text>
        <Text>
          {JSON.stringify(debugRandomEvents(playerContext), null, "\t")}
        </Text>
      </Card>
      <Card>
        <Card.Title>Player Context Editing</Card.Title>
        <Input
          multiline={true}
          onChangeText={setNewPlayerContextState}
          value={newPlayerContextState}
          style={{
            height: 400,
            fontFamily: Platform.select({
              android: "monospace",
              default: "Courier New",
            }),
          }}
        />
        <Button
          onPress={overridePlayerContext}
          title={"Set player context"}
          key="setPlayerContext"
        />
      </Card>

      <Card>
        <Card.Title>Player Context Fixing</Card.Title>
        <Input
          multiline={true}
          onChangeText={setFixingPlayerContextState}
          value={fixingPlayerContextState}
          style={{
            height: 400,
            fontFamily: Platform.select({
              android: "monospace",
              default: "Courier New",
            }),
          }}
        />
        <Button
          onPress={async () => {
            const unserialized = await loadBase64Data(fixingPlayerContextState);
            setFixingPlayerContextState(
              JSON.stringify(unserialized, null, "  "),
            );
          }}
          title={"Load"}
          key="fixPlayerContext"
        />
        <Button
          onPress={() => {
            saveToFile(JSON.parse(fixingPlayerContextState));
          }}
          title={"Save to File"}
          key="getAsFile"
        />
      </Card>

      <Card>
        <Card.Title>Trigger Events</Card.Title>
        {eventButtons}
      </Card>
    </View>
  );
}

registerTransformation(
  [[AttackTarget.Player, CombatStat.AttackDelay]],
  "machinegun_delay",
  TransformationType.Override,
  (state) => (hasFlag(state, "machinegun_mode") ? 0.01 : undefined),
);

registerTransformation(
  [[AttackTarget.Player, CombatStat.Attack]],
  "machinegun_attack",
  TransformationType.Override,
  (state) => (hasFlag(state, "machinegun_mode") ? 1 : undefined),
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
  },
});
